export const CREATE_AUTH_HANDOFF_PARAM = "auth_handoff";

const STORAGE_KEY = "create.auth.handoffs.v1";
const STORAGE_PREFIX = `${STORAGE_KEY}.`;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredCreateAuthHandoff {
  sourceUserId: string;
  createdAt: number;
}

interface StoredCreateAuthHandoffs {
  handoffs: Record<string, StoredCreateAuthHandoff>;
}

export function createCreateAuthIntentId(): string | null {
  try {
    if (typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join(
      ""
    );
  } catch {
    return null;
  }
}

function handoffStorageKey(handoffId: string): string {
  return `${STORAGE_PREFIX}${handoffId}`;
}

function writeHandoff(
  handoffId: string,
  handoff: StoredCreateAuthHandoff
): void {
  const storageKey = handoffStorageKey(handoffId);
  const serialized = JSON.stringify(handoff);
  window.localStorage.setItem(storageKey, serialized);
  if (window.localStorage.getItem(storageKey) !== serialized) {
    throw new Error("Create auth handoff was not persisted");
  }
}

function removeHandoff(handoffId: string): void {
  const storageKey = handoffStorageKey(handoffId);
  window.localStorage.removeItem(storageKey);
  if (window.localStorage.getItem(storageKey) !== null) {
    throw new Error("Create auth handoff was not cleared");
  }
}

function migrateLegacyHandoffs(): void {
  const serialized = window.localStorage.getItem(STORAGE_KEY);
  if (!serialized) return;
  const parsed = JSON.parse(serialized) as Partial<StoredCreateAuthHandoffs>;
  for (const [handoffId, handoff] of Object.entries(parsed.handoffs ?? {})) {
    if (!window.localStorage.getItem(handoffStorageKey(handoffId))) {
      writeHandoff(handoffId, handoff);
    }
  }
  window.localStorage.removeItem(STORAGE_KEY);
}

function readHandoffs(): Record<string, StoredCreateAuthHandoff> {
  migrateLegacyHandoffs();
  const handoffs: Record<string, StoredCreateAuthHandoff> = {};
  for (let index = 0; index < window.localStorage.length; index++) {
    const storageKey = window.localStorage.key(index);
    if (!storageKey?.startsWith(STORAGE_PREFIX)) continue;
    const serialized = window.localStorage.getItem(storageKey);
    if (!serialized) continue;
    const handoff = JSON.parse(serialized) as StoredCreateAuthHandoff;
    const handoffId = storageKey.slice(STORAGE_PREFIX.length);
    if (handoffId) handoffs[handoffId] = handoff;
  }
  return handoffs;
}

function isCurrent(handoff: StoredCreateAuthHandoff): boolean {
  const ageMs = Date.now() - Number(handoff.createdAt);
  return (
    Boolean(handoff.sourceUserId) &&
    Number.isFinite(ageMs) &&
    ageMs >= 0 &&
    ageMs <= MAX_AGE_MS
  );
}

function readCurrentHandoffs(): Record<string, StoredCreateAuthHandoff> {
  const handoffs = readHandoffs();
  const current = Object.fromEntries(
    Object.entries(handoffs).filter(([, handoff]) => isCurrent(handoff))
  );
  for (const handoffId of Object.keys(handoffs)) {
    if (!current[handoffId]) removeHandoff(handoffId);
  }
  return current;
}

/**
 * Creates a browser-local capability that proves an account handoff was
 * initiated by the anonymous identity currently owning the create state.
 */
export function beginCreateAuthHandoff(
  sourceUserId?: string | null
): string | null {
  if (typeof window === "undefined" || !sourceUserId) return null;
  try {
    const handoffId = createCreateAuthIntentId();
    if (!handoffId) return null;
    readCurrentHandoffs();
    writeHandoff(handoffId, { sourceUserId, createdAt: Date.now() });
    return handoffId;
  } catch {
    return null;
  }
}

export function hasCreateAuthHandoff(
  handoffId?: string | null,
  expectedSourceUserId?: string | null
): boolean {
  if (typeof window === "undefined" || !handoffId || !expectedSourceUserId) {
    return false;
  }
  try {
    const handoff = readCurrentHandoffs()[handoffId];
    return Boolean(handoff) && handoff.sourceUserId === expectedSourceUserId;
  } catch {
    return false;
  }
}

export function clearCreateAuthHandoff(handoffId?: string | null): void {
  if (typeof window === "undefined" || !handoffId) return;
  try {
    removeHandoff(handoffId);
  } catch {
    // Best-effort cleanup; records expire even if storage is unavailable.
  }
}

export function clearAllCreateAuthHandoffs(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index++) {
      const storageKey = window.localStorage.key(index);
      if (storageKey?.startsWith(STORAGE_PREFIX)) keys.push(storageKey);
    }
    keys.forEach(storageKey => window.localStorage.removeItem(storageKey));
  } catch {
    // Best-effort cleanup at a real identity boundary.
  }
}
